/**
 * Embedded admin player bar (TASK-054, design §4.6 / §5.10).
 *
 * Sticky-bottom 64 px bar visible whenever the global player context has an
 * active track. Renders cover thumbnail + title/subtitle, play/pause, seek
 * slider, error banner, close button. Drives a hidden `<audio>` element via
 * the `attachHls` helper (hls.js in evergreen browsers, native HLS in
 * Safari/iPad — see ADR-008).
 *
 * Memory-leak guard: every track change OR unmount calls `handle.destroy()`,
 * which in turn invokes `hls.destroy()` for the hls.js path.
 *
 * Keyboard accessibility:
 *   - Play/Pause button has an aria-label and is reachable via Tab.
 *   - Pressing `Space` while the bar is visible toggles play/pause unless
 *     focus is inside an input / textarea / contenteditable surface (so
 *     typing a Space in a filter does not pause playback).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Group,
  Image,
  Paper,
  Slider,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconPlayerTrackNextFilled,
  IconPlayerTrackPrevFilled,
  IconX,
} from '@tabler/icons-react';
import { fadeGain } from '@music-app/shared';
import { useAuth } from '@/lib/auth-provider';
import { usePlayer } from '@/lib/player/usePlayer';
import {
  attachHls,
  type HlsFatalError,
  type HlsHandle,
} from '@/lib/player/hls-loader';
import { fetchTrackPreviewToken } from '@/api/track-preview-token';

const PLACEHOLDER_COVER = '/placeholder-cover.svg';
const BAR_HEIGHT = 64;
/** Volume-ramp tick for the preview crossfade — matches the web player's
 *  cadence (~7 steps/s keeps a 3s ramp audibly smooth). */
const FADE_TICK_MS = 150;

export function PlayerBar() {
  const {
    currentTrack,
    isPlaying,
    togglePlay,
    setIsPlaying,
    close,
    playNext,
    playPrev,
    hasNext,
    hasPrev,
    fadeSeconds,
  } = usePlayer();
  const { accessToken } = useAuth();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const handleRef = useRef<HlsHandle | null>(null);

  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [fatalError, setFatalError] = useState<HlsFatalError | null>(null);

  // Attach / re-attach when the active track changes.
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl || !currentTrack || !accessToken) {
      return;
    }

    setFatalError(null);
    setDuration(0);
    setProgress(0);

    const handle = attachHls({
      audioEl,
      manifestUrl: `/admin/tracks/${currentTrack.id}/playback.m3u8`,
      accessToken,
      // Safari/iPad native-HLS path (BUG-161): mint a short-lived ?token=
      // credential because native playback cannot attach the Bearer header.
      fetchPreviewToken: () =>
        fetchTrackPreviewToken(currentTrack.id).then((r) => r.token),
      onFatalError: (err) => {
        setFatalError(err);
        setIsPlaying(false);
      },
    });
    handleRef.current = handle;

    return () => {
      handle.destroy();
      handleRef.current = null;
    };
  }, [currentTrack, accessToken, setIsPlaying]);

  // Preset crossfade (PRD §4.6.3 rule 8): while a fading queue plays, ramp
  // the volume over the first/last `fadeSeconds` of the track. Interval-driven
  // — `timeupdate` (~4×/s) alone makes a 3s ramp audibly steppy. Respects
  // prefers-reduced-motion (crossfade is a motion effect, design §5).
  // Owns ALL volume writes (the attach effect stays volume-agnostic so a
  // `fadeSeconds` change never re-attaches the stream — review 2026-07-10):
  // fade off → full volume; fade on → open silent, ramp brings it in.
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl || !currentTrack) return;
    if (
      fadeSeconds <= 0 ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      audioEl.volume = 1;
      return;
    }
    const tick = () => {
      const d = audioEl.duration;
      audioEl.volume = fadeGain(
        audioEl.currentTime,
        Number.isFinite(d) ? d : 0,
        fadeSeconds,
      );
    };
    tick();
    const timer = setInterval(tick, FADE_TICK_MS);
    return () => clearInterval(timer);
  }, [currentTrack, fadeSeconds]);

  // Sync `isPlaying` → underlying `<audio>` element. Browsers reject `play()`
  // before the media is ready; the `.catch` swallows the AbortError that
  // surfaces when we destroy hls.js mid-play() and treats every other
  // rejection as a fatal error so the user sees feedback.
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl || !currentTrack) return;
    if (isPlaying) {
      const playResult = audioEl.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch((err: unknown) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setFatalError({
            code: 'unknown',
            message: 'Не удалось начать воспроизведение.',
          });
          setIsPlaying(false);
        });
      }
    } else {
      audioEl.pause();
    }
  }, [isPlaying, currentTrack, setIsPlaying]);

  // Mirror native audio events into context state.
  const handleAudioPlay = useCallback(
    () => setIsPlaying(true),
    [setIsPlaying],
  );
  const handleAudioPause = useCallback(
    () => setIsPlaying(false),
    [setIsPlaying],
  );
  const handleLoadedMetadata = useCallback(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    setDuration(Number.isFinite(audioEl.duration) ? audioEl.duration : 0);
  }, []);
  const handleTimeUpdate = useCallback(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    setProgress(audioEl.currentTime);
  }, []);
  const handleEnded = useCallback(() => {
    setProgress(0);
    // Queue mode: roll on to the next track; single mode: just stop.
    if (hasNext) {
      playNext();
    } else {
      setIsPlaying(false);
    }
  }, [hasNext, playNext, setIsPlaying]);

  // Global Space-to-toggle, scoped to non-input focus targets.
  useEffect(() => {
    if (!currentTrack) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target?.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      togglePlay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentTrack, togglePlay]);

  if (!currentTrack) return null;

  const handleSeek = (value: number) => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    audioEl.currentTime = value;
    setProgress(value);
  };

  return (
    <Paper
      withBorder
      radius={0}
      role="region"
      aria-label="Плеер предпросмотра"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: BAR_HEIGHT,
        zIndex: 200,
        backgroundColor: 'var(--mantine-color-body)',
        borderTop: '1px solid var(--mantine-color-default-border)',
      }}
    >
      <Group
        wrap="nowrap"
        gap="md"
        align="center"
        style={{ height: '100%', padding: '0 16px' }}
      >
        <Image
          src={currentTrack.coverUrl ?? PLACEHOLDER_COVER}
          alt=""
          w={40}
          h={40}
          radius="sm"
          fit="cover"
          fallbackSrc={PLACEHOLDER_COVER}
        />
        <Stack gap={0} style={{ minWidth: 120, maxWidth: 240 }}>
          <Text size="sm" fw={500} truncate="end">
            {currentTrack.title}
          </Text>
          <Text size="xs" c="dimmed" truncate="end">
            {formatSubtitle(currentTrack)}
          </Text>
        </Stack>

        <Group gap={4} wrap="nowrap">
          {hasPrev || hasNext ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="md"
              onClick={playPrev}
              disabled={!hasPrev}
              aria-label="Предыдущий трек"
            >
              <IconPlayerTrackPrevFilled size={16} />
            </ActionIcon>
          ) : null}
          <ActionIcon
            variant="filled"
            color="indigo"
            size="lg"
            radius="xl"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Пауза' : 'Воспроизведение'}
          >
            {isPlaying ? (
              <IconPlayerPauseFilled size={18} />
            ) : (
              <IconPlayerPlayFilled size={18} />
            )}
          </ActionIcon>
          {hasPrev || hasNext ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="md"
              onClick={playNext}
              disabled={!hasNext}
              aria-label="Следующий трек"
            >
              <IconPlayerTrackNextFilled size={16} />
            </ActionIcon>
          ) : null}
        </Group>

        <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" c="dimmed" style={{ width: 40, textAlign: 'right' }}>
            {formatTime(progress)}
          </Text>
          <Slider
            value={progress}
            min={0}
            max={duration || 1}
            step={1}
            onChange={handleSeek}
            label={null}
            size="xs"
            disabled={!duration}
            style={{ flex: 1 }}
            aria-label="Позиция воспроизведения"
          />
          <Text size="xs" c="dimmed" style={{ width: 40 }}>
            {formatTime(duration)}
          </Text>
        </Group>

        {fatalError ? (
          <Alert
            color="red"
            variant="light"
            p="xs"
            style={{ maxWidth: 320 }}
            role="alert"
          >
            <Text size="xs">{fatalError.message}</Text>
          </Alert>
        ) : null}

        <ActionIcon
          variant="subtle"
          color="gray"
          size="md"
          onClick={close}
          aria-label="Закрыть плеер"
        >
          <IconX size={18} />
        </ActionIcon>
      </Group>

      <audio
        ref={audioRef}
        preload="metadata"
        onPlay={handleAudioPlay}
        onPause={handleAudioPause}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
      />
    </Paper>
  );
}

function formatSubtitle(track: {
  artist: string | null;
  bpm?: number | null;
  camelotKey?: string | null;
}): string {
  const parts: string[] = [];
  if (track.artist) parts.push(track.artist);
  if (track.bpm != null) parts.push(`${track.bpm} BPM`);
  if (track.camelotKey) parts.push(track.camelotKey);
  return parts.join(' · ');
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
